import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { Announcement, AnnouncementType, useAnnouncements } from '../api/liveContent';

const STORAGE_KEY = 'mybank-dismissed-announcements';

const styles: Record<AnnouncementType, { bar: string; label: string }> = {
  info: { bar: 'bg-[#0065D1] text-white', label: 'Notice' },
  promotion: { bar: 'bg-[#E0A526] text-[#0B1A36]', label: 'Offer' },
  warning: { bar: 'bg-[#FDF3DD] text-[#8A5A00] border-b border-[#8A5A00]/30', label: 'Warning' },
  alert: { bar: 'bg-[#B42318] text-white', label: 'Important' },
};

/** A dismissal is remembered per id AND updated_at, so an edited message shows again. */
const keyOf = (a: Announcement) => `${a.id}:${a.updatedAt}`;

function readDismissed(): string[] {
  try {
    const v: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * Live notices from the ERP, above the navbar. Renders nothing while loading, on
 * error, or when nothing is live. Hides a notice the moment its end time passes
 * while the page stays open.
 */
const AnnouncementBar: React.FC = () => {
  const { data } = useAnnouncements();
  const [dismissed, setDismissed] = useState<string[]>(readDismissed);
  const [expired, setExpired] = useState<string[]>([]);
  const items = useMemo(() => data ?? [], [data]);

  useEffect(() => {
    const timers = items.flatMap((a) => {
      if (!a.endsAt) return [];
      const ms = Date.parse(a.endsAt) - Date.now();
      // setTimeout overflows past ~24.8 days; the list is refetched every minute anyway.
      if (Number.isNaN(ms) || ms > 2_000_000_000) return [];
      return [window.setTimeout(() => setExpired((e) => [...e, a.id]), Math.max(ms, 0))];
    });
    return () => timers.forEach(window.clearTimeout);
  }, [items]);

  const current = items.find((a) => !expired.includes(a.id) && !(a.dismissible && dismissed.includes(keyOf(a))));
  if (!current) return null;

  const s = styles[current.type];
  const external = current.link ? /^https?:/i.test(current.link.url) : false;
  const linkClass = 'whitespace-nowrap font-semibold underline underline-offset-2';

  const dismiss = () => {
    const next = Array.from(new Set([...readDismissed(), keyOf(current)])).slice(-20);
    setDismissed(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* private mode: dismissed for this view only */
    }
  };

  return (
    <div
      role={current.type === 'alert' || current.type === 'warning' ? 'alert' : 'region'}
      aria-label="Announcement"
      className={`text-sm ${s.bar}`}
    >
      <div className="container flex min-h-[44px] items-center justify-between gap-3 py-1.5">
        <p>
          <span className="sr-only">{s.label}: </span>
          {current.message}
          {current.link ? (
            <>
              {' '}
              {external ? (
                <a href={current.link.url} target="_blank" rel="noopener noreferrer" className={linkClass}>
                  {current.link.label}
                  <span className="sr-only"> (opens in a new tab)</span>
                </a>
              ) : (
                <Link to={current.link.url} className={linkClass}>
                  {current.link.label}
                </Link>
              )}
            </>
          ) : null}
        </p>
        {current.dismissible ? (
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss announcement"
            className="-mr-2 flex h-11 w-11 shrink-0 items-center justify-center rounded hover:bg-black/10"
          >
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default AnnouncementBar;
